/**
 * Hero illustration — ported from planapus-lab's `DocsToOutcomes`
 * (see /Users/parsa/Claude Code/planapus-lab/src/components/brand-v2/
 *  DocsToOutcomes.tsx). The previous "Composition + arrow-that-never-
 * lands" side-accent was design-flagged as detached and irrelevant.
 *
 * Knoebel/Pam-Klein-adjacent abstraction:
 *   - Left: 12 chaotic angular shapes in desaturated tones, vibrating
 *     in place with jitter on x/y/rotation.
 *   - Middle: 10 colza-yellow particle squares streaming left → right
 *     with a sinusoidal bob.
 *   - Right: Anima-Mundi framed panel with a 16-spoke sun-ray burst.
 *     Inside the colza center, five completion bars fade in in
 *     sequence; the top bar is the Knoebel green — its arrival reads
 *     as "outcome complete" without a checkmark.
 *
 * Five prohibitions intact: no gradients, no shadows, no border radius,
 * orthogonal geometry. Motion via jitter + translation + opacity only,
 * never shape morph. Gated by `prefers-reduced-motion`.
 */

import React, {useEffect, useRef, useState} from 'react';

interface Props {
  size?: number;
  className?: string;
}

export default function CoordinationIllustration({size = 440, className}: Props) {
  const [reducedMotion, setReducedMotion] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [t, setT] = useState(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    setMounted(true);
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(media.matches);
    const listener = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    if (!mounted || reducedMotion) return;
    let alive = true;
    let last = performance.now();
    const tick = (now: number) => {
      if (!alive) return;
      const dt = now - last;
      last = now;
      setT((prev) => (prev + dt / 5000) % 1);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      alive = false;
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [mounted, reducedMotion]);

  const phase = mounted && !reducedMotion ? t : 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 520 520"
      className={className}
      role="img"
      aria-label="An abstract illustration of jumbled coordination work resolving into an ordered outcome."
      style={{background: 'transparent', maxWidth: '100%'}}
    >
      {/* Left — chaotic cluster of 12 desaturated angular forms */}
      <g aria-hidden>
        {CHAOS.map((s, i) => {
          const jitterX = mounted ? Math.sin(phase * Math.PI * 2 + i * 0.8) * 4 : 0;
          const jitterY = mounted ? Math.cos(phase * Math.PI * 2 + i * 1.1) * 3 : 0;
          const jitterR = mounted ? Math.sin(phase * Math.PI * 2 + i) * 3 : 0;
          return (
            <g
              key={i}
              transform={`translate(${s.x + jitterX} ${s.y + jitterY}) rotate(${s.r + jitterR})`}
            >
              <ChaosShape shape={s.shape} fill={s.fill} />
            </g>
          );
        })}
      </g>

      {/* Middle — colza-yellow particles streaming left → right */}
      <g aria-hidden>
        {Array.from({length: 10}).map((_, i) => {
          const base = (i / 10 + phase) % 1;
          const x = 180 + base * 140;
          const y = 200 + Math.sin(base * Math.PI * 2 + i) * 28;
          const opacity = Math.sin(base * Math.PI) * 0.9 + 0.1;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={8}
              height={8}
              fill="var(--motif-colza)"
              opacity={opacity}
            />
          );
        })}
      </g>

      {/* Right — Anima Mundi framed panel + sun rays */}
      <g transform="translate(320 110)" aria-hidden>
        <SunRays phase={phase} />
        <OrderedPanel phase={phase} mounted={mounted} />
      </g>
    </svg>
  );
}

/* ─────────────────────────── Subparts ─────────────────────────── */

type ChaosShapeKind = 'triangle' | 'square' | 'circle' | 'bar' | 'chevron';

const CHAOS: Array<{
  x: number;
  y: number;
  r: number;
  shape: ChaosShapeKind;
  fill: string;
}> = [
  {x: 30,  y: 100, r: 18,  shape: 'triangle', fill: '#5E6678'},
  {x: 80,  y: 80,  r: -10, shape: 'square',   fill: '#7A6B52'},
  {x: 130, y: 120, r: 22,  shape: 'circle',   fill: '#8B5C4A'},
  {x: 40,  y: 170, r: -24, shape: 'bar',      fill: '#4A4E5A'},
  {x: 100, y: 200, r: 14,  shape: 'chevron',  fill: '#6A5840'},
  {x: 160, y: 180, r: -6,  shape: 'triangle', fill: '#5D4B63'},
  {x: 60,  y: 260, r: 35,  shape: 'square',   fill: '#3F4A5C'},
  {x: 125, y: 275, r: -18, shape: 'bar',      fill: '#7E6C58'},
  {x: 30,  y: 330, r: 5,   shape: 'circle',   fill: '#614F5E'},
  {x: 100, y: 360, r: 20,  shape: 'triangle', fill: '#5F5544'},
  {x: 160, y: 340, r: -30, shape: 'square',   fill: '#463843'},
  {x: 60,  y: 410, r: 10,  shape: 'chevron',  fill: '#3C4858'},
];

function ChaosShape({shape, fill}: {shape: ChaosShapeKind; fill: string}) {
  switch (shape) {
    case 'triangle':
      return <polygon points="0,-18 20,14 -20,14" fill={fill} />;
    case 'square':
      return <rect x="-16" y="-16" width="32" height="32" fill={fill} />;
    case 'circle':
      return <circle cx="0" cy="0" r="15" fill={fill} />;
    case 'bar':
      return <rect x="-22" y="-6" width="44" height="12" fill={fill} />;
    case 'chevron':
      return (
        <polygon points="-18,-10 6,-10 18,0 6,10 -18,10 -6,0" fill={fill} />
      );
  }
}

function OrderedPanel({phase, mounted}: {phase: number; mounted: boolean}) {
  const bars: Array<{y: number; width: number; fill: string}> = [
    {y: 190, width: 98,  fill: 'var(--motif-ink-blue)'},
    {y: 168, width: 78,  fill: 'var(--motif-ink-blue)'},
    {y: 146, width: 114, fill: 'var(--motif-vermillion)'},
    {y: 124, width: 88,  fill: 'var(--motif-ink-blue)'},
    {y: 102, width: 128, fill: 'var(--knoebel-green)'},
  ];

  return (
    <g>
      <rect x="12"  y="12"  width="176" height="276" fill="var(--bg-elev)" />
      <rect x="18"  y="18"  width="164" height="24"  fill="var(--motif-ink-blue)" />
      <rect x="18"  y="42"  width="24"  height="218" fill="var(--motif-clay)" />
      <rect x="174" y="42"  width="8"   height="218" fill="var(--motif-ink-blue)" />
      <rect x="42"  y="42"  width="132" height="218" fill="var(--motif-colza)" />
      <rect x="18"  y="260" width="164" height="16"  fill="var(--accent)" />
      <rect x="18"  y="260" width="20"  height="16"  fill="var(--motif-vermillion)" />

      {bars.map((bar, i) => {
        const delay = i * 0.15;
        const local = mounted ? (phase - delay + 1) % 1 : 0.6;
        const appeared = local > 0.08;
        const opacity = mounted
          ? appeared
            ? Math.min(1, (local - 0.08) / 0.12)
            : 0
          : 1;
        const offset = mounted ? (1 - opacity) * 8 : 0;
        return (
          <rect
            key={i}
            x={54 + offset}
            y={bar.y}
            width={bar.width}
            height={14}
            fill={bar.fill}
            opacity={opacity}
          />
        );
      })}
    </g>
  );
}

function SunRays({phase}: {phase: number}) {
  return (
    <g transform="translate(100 150)">
      {Array.from({length: 16}).map((_, i) => {
        const angle = (i / 16) * Math.PI * 2;
        const pulse = (Math.sin(phase * Math.PI * 2 + i * 0.3) + 1) / 2;
        const inner = 130;
        const outer = 150 + Math.round(pulse * 10);
        const x1 = Math.round(Math.cos(angle) * inner);
        const y1 = Math.round(Math.sin(angle) * inner);
        const x2 = Math.round(Math.cos(angle) * outer);
        const y2 = Math.round(Math.sin(angle) * outer);
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="var(--motif-colza)"
            strokeWidth="6"
            strokeLinecap="square"
            opacity={0.8}
          />
        );
      })}
    </g>
  );
}
